<template>
    <div>
        <div class="forms__field_input--date__time__clock">
            <!-- HOURS -->
            <input
                ref="hoursRef"
                v-model="hoursStr"
                type="text"
                class="forms__field_input--date__time__hours"
                :class="[sizeClass]"
                :placeholder="nullableTime ? 'HH' : ''"
                :disabled="disabled"
                @mousedown.prevent="onMouseDownHours"
                @focus="onFocusHours"
                @input="onInputHours"
                @blur="onBlurHours"
                @keyup="onKeyupHours"
            />
            <div class="forms__field_input--date__time__separator">:</div>
            <!-- MINUTES -->
            <input
                ref="minutesRef"
                v-model="minutesStr"
                type="text"
                class="forms__field_input--date__time__minutes"
                :class="[sizeClass]"
                :placeholder="nullableTime ? 'MM' : ''"
                :disabled="disabled"
                @mousedown.prevent="onMouseDownMinutes"
                @focus="onFocusMinutes"
                @input="onInputMinutes"
                @blur="onBlurMinutes"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, Ref, watch, nextTick } from 'vue';

type ComponentSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';

interface Props {
    modelValue?: string | null; // "HH:MM" or null
    size?: ComponentSize;
    nullableTime?: boolean;
    disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    size: 'base',
    nullableTime: false,
    disabled: false,
});

const emit = defineEmits<{
    (event: 'update:modelValue', time: string): void;
    (event: 'change', time: string): void;
}>();

const sizeClass: Ref<string> = computed((): string => {
    const sizes = {
        xs: 'forms__field_input--date__time--xs',
        sm: 'forms__field_input--date__time--sm',
        base: 'forms__field_input--date__time--base',
        lg: 'forms__field_input--date__time--lg',
        xl: 'forms__field_input--date__time--xl',
    };
    return sizes[props.size] ?? sizes.base;
});

/**
 * Refs to the two input elements (for focus/selection).
 */
const hoursRef = ref<HTMLInputElement | null>(null);
const minutesRef = ref<HTMLInputElement | null>(null);

/**
 * Local reactive strings
 */
const hoursStr = ref('00');
const minutesStr = ref('00');

/**
 * Parse "HH:MM" into hours / minutes strings
 */
function parseTime(value: string | null | undefined) {
    if (!value) {
        // For nullableTime, return empty strings instead of 00:00
        if (props.nullableTime) {
            return { hours: '', minutes: '' };
        }
        return { hours: '00', minutes: '00' };
    }
    const [h = '00', m = '00'] = value.split(':');
    return {
        hours: h.padStart(2, '0'),
        minutes: m.padStart(2, '0'),
    };
}

/**
 * Emit final "HH:MM" to parent
 */
function updateValue() {
    // If nullableTime and both fields are empty, emit empty string
    if (props.nullableTime && (!hoursStr.value || !minutesStr.value)) {
        emit('update:modelValue', '');
        emit('change', '');
        return;
    }

    const finalValue = `${hoursStr.value}:${minutesStr.value}`;
    emit('update:modelValue', finalValue);
    emit('change', finalValue);
}

/**
 * Watch for external modelValue changes
 */
watch(
    () => props.modelValue,
    (newVal) => {
        const { hours, minutes } = parseTime(newVal);
        hoursStr.value = hours;
        minutesStr.value = minutes;
    },
    { immediate: true },
);

/*
 * HOURS logic
 */

/**
 * onMouseDownHours: prevent default so the cursor doesn't jump
 * and select the entire content
 */
function onMouseDownHours(e: MouseEvent) {
    e.preventDefault();
    hoursRef.value?.select();
}

/**
 * onFocusHours: also ensure all text is selected
 */
function onFocusHours() {
    // nextTick ensures we wait until the element is focused
    nextTick(() => {
        hoursRef.value?.select();
    });
}

/**
 * onInputHours: allow only digits, max 2
 */
function onInputHours(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    hoursStr.value = val.replace(/\D/g, '').slice(0, 2);
}

/**
 * onKeyupHours: focus minutes if 2 digits typed
 */
function onKeyupHours() {
    if (hoursStr.value.length === 2 && minutesRef.value) {
        minutesRef.value.focus();
    }
}

/**
 * onBlurHours: normalize 0–23, pad with zero, emit
 */
function onBlurHours() {
    let h = parseInt(hoursStr.value || '0', 10);
    if (h < 0) h = 0;
    if (h > 23) h = 23;
    hoursStr.value = h.toString().padStart(2, '0');
    updateValue();
}

/*
 * MINUTES logic
 */

/**
 * onMouseDownMinutes: same approach as hours - select all.
 */
function onMouseDownMinutes(e: MouseEvent) {
    e.preventDefault();
    minutesRef.value?.select();
}

/**
 * onFocusMinutes: select entire text
 */
function onFocusMinutes() {
    nextTick(() => {
        minutesRef.value?.select();
    });
}

/**
 * onInputMinutes: allow only digits, max 2
 */
function onInputMinutes(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    minutesStr.value = val.replace(/\D/g, '').slice(0, 2);
}

/**
 * onBlurMinutes: normalize 0–59, pad with zero, emit
 */
function onBlurMinutes() {
    let m = parseInt(minutesStr.value || '0', 10);
    if (m < 0) m = 0;
    if (m > 59) m = 59;
    minutesStr.value = m.toString().padStart(2, '0');
    updateValue();
}
</script>
